import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import { ArticleHero } from "../ArticleHero";
import HeroImg from "../../assets/images/newborn-pink-studio-portrait.webp";
import newborn1 from "../../assets/images/newborn-dinosaur-prop-studio-portrait.webp";
import newbornAutumnBasketImage from "../../assets/images/newborn-autumn-basket-portrait.webp";
import newbornBlueImage from "../../assets/images/newborn-blue-studio-portrait.webp";
import newbornPlaidImage from "../../assets/images/newborn-plaid-outfit-portrait.webp";
import newbornPumpkinImage from "../../assets/images/newborn-pumpkin-wrap-portrait.webp";
import newbornBearImage from "../../assets/images/newborn-sleeping-bear-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";

const articleImages: Record<"hero" | "afterTwoWeeks" | "afterThreeWeeks" | "olderBaby", string | undefined> = {
  hero: HeroImg,
  afterTwoWeeks: newborn1,
  afterThreeWeeks: newbornPlaidImage,
  olderBaby: newbornBlueImage,
};

const ageExamples = [
  {
    age: "5–14 days",
    title: "Classic newborn portraits",
    text: "Babies often sleep for longer stretches, which can make wrapped and gently curled portraits easier to create.",
  },
  {
    age: "2–3 weeks",
    title: "More awake moments",
    text: "There may be more stretching and alert time, so the session can include both sleepy portraits and little expressions.",
  },
  {
    age: "3–6 weeks",
    title: "A flexible newborn session",
    text: "We plan around feeding, settling and the positions that feel comfortable for your baby that day.",
  },
  {
    age: "6+ weeks",
    title: "Connection and expression",
    text: "This is a wonderful age for eye contact, parent-and-baby photographs and relaxed, natural portraits.",
  },
];

const relatedGuides = [
  {
    title: "What Happens During a Newborn Session",
    href: "/resources/newborn/what-happens-during-a-newborn-session/",
    image: newbornBearImage,
    imageAlt: "Sleeping newborn baby in a warm bear-themed portrait",
  },
  {
    title: "How to Prepare for Your Newborn Session",
    href: "/resources/newborn/how-to-prepare-for-your-newborn-session/",
    image: newbornPumpkinImage,
    imageAlt: "Newborn baby wrapped in rust-colored fabric beside autumn pumpkins",
  },
  {
    title: "What If My Baby Doesn’t Sleep?",
    href: "/resources/newborn/what-happens-during-a-newborn-session/#baby-awake",
    image: newbornAutumnBasketImage,
    imageAlt: "Awake newborn baby posed in a basket with autumn decorations",
  },
];

export const BestAgeForNewbornPhotos = (): ReactElement => (
  <main className="newborn-page newborn-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Newborn", to: "/resources/newborn/" },
          { label: "Best Age for Newborn Photos" },
        ]}
        category="Newborn guide"
        description="The first two weeks can be ideal for classic newborn portraits, but beautiful photographs are possible well beyond that window."
        imageAlt="Sleeping newborn baby photographed during a newborn session"
        imagePosition="center 42%"
        imageSrc={articleImages.hero ?? HeroImg}
        readingTime="7 min read"
        title="Best Age for Newborn Photos"
        updatedAt="2026-08-02"
        updatedLabel="Updated August 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout newborn-article-callout--answer">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              For sleepy, curled newborn photos, 5–14 days is often the easiest time to schedule. If your baby is older,
              you have not missed your chance. Sessions after 21 days—and even after the first month—are simply planned
              at a different pace, with your baby’s comfort leading every decision.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#five-to-fourteen-days">Why 5–14 days often works well</a>
            </li>
            <li>
              <a href="#after-two-weeks">What changes after two weeks</a>
            </li>
            <li>
              <a href="#after-three-weeks">Sessions after 21 days</a>
            </li>
            <li>
              <a href="#older-than-month">If your baby is older than a month</a>
            </li>
            <li>
              <a href="#age-examples">What to expect at each age</a>
            </li>
            <li>
              <a href="#best-results">How to plan for the best results</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section" id="five-to-fourteen-days">
          <h2>Why 5–14 days often works well</h2>
          <p>
            During the first two weeks, many babies sleep for longer stretches and may settle more easily into the snug,
            curled positions often seen in classic newborn photography. This can make wrapping and simple posed
            portraits easier to do at a gentle pace.
          </p>
          <p>
            This is a helpful window, not a deadline. Feeding, your recovery, an early or late arrival, and how your
            family is doing matter more than finding one “perfect” day.
          </p>

          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>A baby-led session</h3>
              <p>I follow your baby’s cues, leave room for feeding and soothing, and never force a pose.</p>
            </div>
          </aside>
        </section>

        <section className="newborn-article-section newborn-article-split" id="after-two-weeks">
          <div>
            <p className="newborn-article-kicker">AFTER THE FIRST TWO WEEKS</p>
            <h2>What changes after two weeks?</h2>
            <p>
              Babies gradually become more alert, may stretch more often and may sleep a little more lightly. That does
              not mean you are too late for newborn photos. It simply means we make room for awake portraits, little
              hand and foot details, and moments with parents.
            </p>
            <p>
              Wrapped photographs are often still possible. Rather than aiming for every tightly curled pose, I use
              comfortable positions that work naturally for your baby.
            </p>
          </div>

          <ArticlePhoto
            alt="Sleeping newborn baby posed with a green dinosaur prop"
            className="newborn-article-split__photo"
            label="PHOTO: AFTER TWO WEEKS"
            src={articleImages.afterTwoWeeks}
          />
        </section>

        <section
          className="newborn-article-section newborn-article-split newborn-article-split--reverse"
          id="after-three-weeks"
        >
          <ArticlePhoto
            alt="Baby older than three weeks photographed in a relaxed pose"
            className="newborn-article-split__photo"
            label="PHOTO: 3+ WEEKS"
            src={articleImages.afterThreeWeeks}
          />

          <div>
            <p className="newborn-article-kicker">AFTER 21 DAYS</p>
            <h2>Can we still do a newborn session?</h2>
            <p>
              Absolutely. A session after 21 days can still be beautiful. Older newborns are often more awake, may need
              extra time to settle and may not be comfortable in every traditional pose, so I adjust the session rather
              than asking your baby to fit a fixed plan.
            </p>
            <p>
              We may use more wrapping, simpler poses, close-up details and photographs with parents. The images can
              still feel unmistakably newborn, while being true to this stage of your baby’s first weeks.
            </p>
          </div>
        </section>

        <section className="newborn-article-section newborn-article-split" id="older-than-month">
          <div>
            <p className="newborn-article-kicker">ONE MONTH AND OLDER</p>
            <h2>What if my baby is older than a month?</h2>
            <p>
              Please do not feel that you missed your chance. At one month and beyond, babies often give us longer eye
              contact, more expression and beautiful connection with their parents. Those are meaningful photographs
              too.
            </p>
            <p>
              Before booking, we can talk about your baby’s age and the images you are drawn to. I will explain what is
              realistic for this stage and suggest either an adapted newborn session or a relaxed baby session.
            </p>
          </div>

          <ArticlePhoto
            alt="Baby older than one month looking toward the camera"
            className="newborn-article-split__photo"
            label="PHOTO: OLDER BABY"
            src={articleImages.olderBaby}
          />
        </section>

        <section className="newborn-article-section" id="age-examples">
          <p className="newborn-article-kicker">A FLEXIBLE APPROACH</p>
          <h2>What to expect at each age</h2>
          <div className="newborn-age-grid">
            {ageExamples.map((example) => (
              <article key={example.age}>
                <span>{example.age}</span>
                <h3>{example.title}</h3>
                <p>{example.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="newborn-article-section" id="best-results">
          <p className="newborn-article-kicker">PLANNING YOUR SESSION</p>
          <h2>How to get the best results</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>
                Book during pregnancy if you can, then confirm the date after your baby arrives. This gives us the best
                chance of holding a spot near your due date.
              </span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>If your baby is already here, contact me anyway—we will choose the best available option.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>
                Share your baby’s age, due date and anything that would help me plan a calm, comfortable session for
                your family.
              </span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>
                Keep expectations flexible. Your baby’s comfort and safety always come before a particular pose.
              </span>
            </li>
          </ul>
        </section>

        <section className="newborn-article-cta">
          <div>
            <p className="newborn-article-kicker">YOUR BABY IS NOT TOO LATE</p>
            <h2>Wondering which session is right for your baby?</h2>
            <p>
              Compare the newborn packages and finished-image options. If you are unsure which experience fits your
              baby’s age, send me a question and I’ll help you choose.
            </p>
          </div>
          <GuideCtaActions category="newborn" />
        </section>

        <section className="newborn-related-guides">
          <header>
            <h2>Related newborn guides</h2>
            <Link to="/resources/newborn/">
              View all guides <UiIcon name="arrow" />
            </Link>
          </header>

          <div>
            {relatedGuides.map((guide, index) => (
              <Link className="newborn-related-guide" key={guide.href} to={guide.href}>
                <ArticlePhoto
                  alt={guide.imageAlt}
                  className={`newborn-related-guide__photo newborn-related-guide__photo--${index + 1}`}
                  label="PHOTO"
                  src={guide.image}
                />
                <span>{guide.title}</span>
                <UiIcon name="arrow" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  </main>
);
